import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/utils/api";

import NewShuttlelessDetails from "./ViewForms/NewShuttlelessDetails";
import CFCSubsidyDetails from "./ViewForms/CFCSubsidyDetails";
import OldConventionalDetails from "./ViewForms/OldConventionalDetails";
import AirJetLoomView from "./ViewForms/AirJetLoomView";

const ApplicationDetails = () => {
  const { type, id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApplicationDetails();
  }, [type, id]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get(`/applications/${type}/${id}`);

      setApplication(res.data.data || res.data);
    } catch (error) {
      console.error("Error fetching application details:", error);
      setError("Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  const renderApplicationDetails = () => {
    if (!application) return null;

    switch (type) {
      case "modernization":
        return (
          <OldConventionalDetails application={application} view_page={true} />
        );
      case "new_shuttleless":
        return <NewShuttlelessDetails application={application} />;
      case "cfc_subsidy":
        return <CFCSubsidyDetails application={application} />;
      case "cfc_subsidies":
        return <CFCSubsidyDetails application={application} />;
      case "new_airjetlooms":
        return <AirJetLoomView application={application} viewPage={true} />;
      default:
        return (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <h2 className="text-xl font-bold mb-2">
                  Unknown Application Type
                </h2>
                <p className="text-muted-foreground mb-4">
                  The application type "{type}" is not recognized.
                </p>
                <Button asChild>
                  <Link to="/dashboard">Back to Dashboard</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-lg">Loading application details...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <h2 className="text-xl font-bold mb-2">
                Error Loading Application
              </h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={fetchApplicationDetails} variant="outline">
                  Try Again
                </Button>
                <Button asChild>
                  <Link to="/dashboard">Back to Dashboard</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No application found
  if (!application) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <h2 className="text-xl font-bold mb-2">Application Not Found</h2>
              <p className="text-muted-foreground mb-4">
                The requested application could not be found.
              </p>
              <Button asChild>
                <Link to="/dashboard">Back to Dashboard</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render the specific application details
  return renderApplicationDetails();
};

export default ApplicationDetails;
